import type { ComponentProps } from "react";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { tv } from "tailwind-variants";

import { icons } from "@/ui/icons";
import { focusRing } from "./interaction";

const accordionVariants = tv({
  slots: {
    item: "border-b border-line last:border-b-0",
    icon: "pointer-events-none size-6 shrink-0 translate-y-0.5 transition-transform duration-200",
    header: "flex",
    trigger: [
      "group flex flex-1 cursor-pointer items-start justify-between gap-4 rounded-md px-1 py-4 text-left text-lg font-semibold",
      "transition-colors hover:text-accent",
      focusRing,
      "disabled:pointer-events-none disabled:opacity-50 sm:text-xl",
    ],
    content:
      "overflow-hidden pb-4 text-content-secondary data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down sm:text-xl",
  },
});

const { content, header, icon, item, trigger } = accordionVariants();

const Root = ({ ...props }: ComponentProps<typeof AccordionPrimitive.Root>) => {
  return <AccordionPrimitive.Root data-slot="accordion" {...props} />;
};

const Header = ({ className, ...props }: ComponentProps<typeof AccordionPrimitive.Header>) => {
  return <AccordionPrimitive.Header className={header({ className })} {...props} />;
};

const Item = ({ className, ...props }: ComponentProps<typeof AccordionPrimitive.Item>) => {
  return (
    <AccordionPrimitive.Item
      className={item({ className })}
      data-slot="accordion-item"
      {...props}
    />
  );
};

const Trigger = ({
  children,
  className,
  ...props
}: ComponentProps<typeof AccordionPrimitive.Trigger>) => {
  return (
    <AccordionPrimitive.Trigger
      className={trigger({ className })}
      data-slot="accordion-trigger"
      {...props}
    >
      {children}
    </AccordionPrimitive.Trigger>
  );
};

const Icon = ({ className, ...props }: ComponentProps<"svg">) => {
  return (
    <>
      <icons.PlusCircleIcon
        className={icon({
          className: ["group-data-[state=closed]:block group-data-[state=open]:hidden", className],
        })}
        {...props}
      />
      <icons.MinusCircleIcon
        className={icon({
          className: ["group-data-[state=closed]:hidden group-data-[state=open]:block", className],
        })}
        {...props}
      />
    </>
  );
};

const Content = ({ className, ...props }: ComponentProps<typeof AccordionPrimitive.Content>) => {
  return (
    <AccordionPrimitive.Content
      className={content({ className })}
      data-slot="accordion-content"
      {...props}
    />
  );
};

export const Accordion = { Root, Content, Item, Trigger, Header, Icon };
